import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import type {
  ReliabilityBand,
  ReliabilityComponentKey,
  ReliabilityProps,
} from "@/types/creator-analytics"

export interface CreatorAnalyticsReliabilityCardProps {
  reliability: ReliabilityProps
}

const COMPONENT_ORDER: ReliabilityComponentKey[] = [
  "on_time",
  "completion",
  "approval",
]

/**
 * OKS-64 — Reliability scalar + traffic-light badge + 3-row
 * breakdown grid. Renders in BOTH OAuth and manual-only modes
 * (computed from brand-private collab/submission data;
 * independent of OAuth state).
 *
 * N/A branch: scalar is `—`, badge reads `N/A` (muted variant),
 * breakdown rows render `0 of N · —%`, helper text below.
 *
 * Per UX Spec §2: scalar `text-5xl tabular-nums`, badge inline-
 * right. Mobile drops to `text-4xl`; breakdown grid stacks to
 * `grid-cols-1`.
 */
export function CreatorAnalyticsReliabilityCard({
  reliability,
}: CreatorAnalyticsReliabilityCardProps) {
  const { t } = useTranslation()

  return (
    <Card
      data-testid="creator-analytics-reliability"
      aria-label={t("crew.analytics.reliability.section_aria")}
    >
      <CardHeader className="flex flex-row items-baseline justify-between">
        <CardTitle>{t("crew.analytics.reliability.title")}</CardTitle>
        <span className="text-muted-foreground text-sm">
          {t("crew.analytics.reliability.caption", {
            window: t(`crew.analytics.window.${reliability.window}`),
          })}
        </span>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="flex flex-row items-baseline gap-4">
          <span
            className={cn(
              "font-semibold tabular-nums",
              reliability.na
                ? "text-muted-foreground text-4xl"
                : "text-4xl sm:text-5xl",
            )}
            data-testid="creator-analytics-reliability-score"
            aria-label={
              reliability.na
                ? t("crew.analytics.reliability.band.na")
                : t("crew.analytics.reliability.score_aria", {
                    score: reliability.score,
                    band: t(
                      `crew.analytics.reliability.band.${reliability.band}`,
                    ),
                  })
            }
          >
            {reliability.na
              ? t("crew.analytics.reliability.score_na")
              : reliability.score}
          </span>
          <Badge
            variant={badgeVariantFor(reliability.band)}
            data-testid="creator-analytics-reliability-badge"
            data-band={reliability.band}
          >
            {t(`crew.analytics.reliability.band.${reliability.band}`)}
          </Badge>
        </div>

        {reliability.na && (
          <div
            data-testid="creator-analytics-reliability-empty"
            className="text-muted-foreground text-sm"
          >
            <p className="font-medium">
              {t("crew.analytics.reliability.empty.title")}
            </p>
            <p>{t("crew.analytics.reliability.empty.body")}</p>
          </div>
        )}

        <dl
          className="grid grid-cols-1 gap-4 sm:grid-cols-3"
          data-testid="creator-analytics-reliability-breakdown"
        >
          {COMPONENT_ORDER.map((key) => {
            const component = reliability.components[key]
            return (
              <div
                key={key}
                data-testid={`creator-analytics-reliability-component-${key}`}
                className="flex flex-col gap-1"
              >
                <dt className="text-muted-foreground text-xs tracking-wide uppercase">
                  {t(`crew.analytics.reliability.breakdown.${key}.label`)}
                </dt>
                <dd className="text-foreground text-2xl font-medium tabular-nums">
                  {component.rate === null
                    ? t("crew.analytics.reliability.rate_unavailable")
                    : `${Math.round(component.rate * 100)}%`}
                </dd>
                <dd className="text-muted-foreground text-xs">
                  {t(`crew.analytics.reliability.breakdown.${key}.caption`, {
                    numerator: component.numerator,
                    denominator: component.denominator,
                  })}
                </dd>
              </div>
            )
          })}
        </dl>
      </CardContent>
    </Card>
  )
}

function badgeVariantFor(
  band: ReliabilityBand,
): "default" | "secondary" | "destructive" | "outline" {
  switch (band) {
    case "excellent":
      return "default"
    case "good":
      return "secondary"
    case "at_risk":
      return "destructive"
    case "na":
    default:
      return "outline"
  }
}

export default CreatorAnalyticsReliabilityCard
